/**
 * QaCaseEditModal.tsx
 *
 * Large, fixed-height modal wrapper around QaCaseEditor (the shared create/edit
 * editor for a patients_qa case). Mirrors the patient-Notes "expanded" view: a
 * centered overlay box at a large fixed height containing the full editor.
 *
 * Used by:
 *   - Admin /qa page: the expanded view (opened from the side panel's expand
 *     button) and the "New Q/A Case" create flow.
 *   - Patient detail QA tab: row click + "New QA Case".
 */
import { useEffect } from 'react';
import { PHONE_SHEET_PANEL, PHONE_SHEET_WRAPPER } from '@/components/ui/Modal';
import { useBodyScrollLock } from '@/hooks/useBodyScrollLock';
import { useIsBelow } from '@/hooks/useBreakpoint';
import { useVisualViewportVars } from '@/hooks/useVisualViewportVars';
import { QaCaseEditor } from './QaCaseEditor';

interface QaCaseEditModalProps {
  /** Existing case id → edit mode. null → create mode. */
  caseId: number | null;
  /** When opening in create mode from the patient tab, locks the Patient field. */
  patientId?: number;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSaved?: (id: number) => void;
}

export function QaCaseEditModal({
  caseId,
  patientId,
  open,
  onOpenChange,
  onSaved,
}: QaCaseEditModalProps) {
  /**
   * Both hooks above the `if (!open)` return, and both unconditional. `useIsBelow` and
   * `useVisualViewportVars` are subscription hooks, and a call placed after an early return —
   * or behind a `&&` — changes the render-to-render hook count. That is the shape that threw
   * React #310 and blanked every page in this app, and `react-hooks/rules-of-hooks` is an
   * error here so it cannot come back quietly.
   *
   * Why they are needed at all (SCRUM-1441): the panel below is `h-[85vh]`, and `vh` is measured
   * against the LAYOUT viewport, which the on-screen keyboard does not shrink. With the keyboard
   * up, the bottom 15vh of the modal plus its whole Cancel/Save footer sit below the visible
   * band — which is the reported "the Reason checkboxes are half hidden behind the footer". The
   * form area and the footer are flow siblings inside a `flex flex-col` and cannot overlap; what
   * moved out of view was the modal itself.
   */
  const isPhone = useIsBelow('md');
  useVisualViewportVars(open && isPhone);
  useBodyScrollLock(open);

  // Close on Escape, matching the app's other overlays.
  useEffect(() => {
    if (!open) return;
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') onOpenChange(false);
    }
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open, onOpenChange]);

  if (!open) return null;

  return (
    <div
      // Below `md` this becomes the shared phone sheet: pinned to the visible viewport, full
      // bleed, with `pt-sheet-inset` leaving a strip of backdrop to tap. The tokens are imported
      // from Modal rather than copied, so the two geometries cannot drift — this modal is
      // hand-rolled because converting it to `Modal` would restructure QaCaseEditor's own header
      // and footer, not because its sheet should behave differently.
      className={`fixed inset-0 z-50 grid place-items-center bg-black/60 p-4 backdrop-blur-sm ${PHONE_SHEET_WRAPPER} max-md:grid-cols-1 max-md:items-stretch max-md:p-0`}
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) onOpenChange(false);
      }}
    >
      <div
        className={`flex h-[85vh] w-[92vw] max-w-5xl flex-col overflow-hidden rounded-lg border border-slate-200 bg-white shadow-2xl ${PHONE_SHEET_PANEL} max-md:w-full max-md:border-0`}
      >
        <QaCaseEditor
          caseId={caseId}
          patientId={patientId}
          onClose={() => onOpenChange(false)}
          onSaved={onSaved}
        />
      </div>
    </div>
  );
}

export default QaCaseEditModal;
